/** TanStack Query keys — one place so invalidation stays consistent. */
export const qk = {
  memberships: (userId: string | null) => ['memberships', userId] as const,
  myInvitations: (userId: string | null) => ['myInvitations', userId] as const,
  settings: (c: string) => ['settings', c] as const,
  members: (c: string) => ['members', c] as const,
  invitations: (c: string) => ['invitations', c] as const,
  currencies: () => ['currencies'] as const,
  marketplaces: () => ['marketplaces'] as const,
  units: (c: string) => ['units', c] as const,
  taxRates: (c: string) => ['taxRates', c] as const,
  sequences: (c: string) => ['sequences', c] as const,
  warehouses: (c: string) => ['warehouses', c] as const,
  infra: (c: string) => ['infra', c] as const,
  audit: (c: string) => ['audit', c] as const,
  notifications: (c: string) => ['notifications', c] as const,
  notificationRules: (c: string) => ['notificationRules', c] as const,
  // accounting — everything under ['acc', company] is invalidated after money movements
  acc: (c: string) => ['acc', c] as const,
  contacts: (c: string) => ['acc', c, 'contacts'] as const,
  openItems: (c: string, contactId?: string) => ['acc', c, 'openItems', contactId ?? 'all'] as const,
  openPayments: (c: string, contactId?: string) => ['acc', c, 'openPayments', contactId ?? 'all'] as const,
  statement: (c: string, contactId: string) => ['acc', c, 'statement', contactId] as const,
  search: (c: string, term: string) => ['acc', c, 'search', term] as const,
  categories: (c: string) => ['acc', c, 'categories'] as const,
  accounts: (c: string) => ['acc', c, 'accounts'] as const,
  ledger: (c: string, accountId: string) => ['acc', c, 'ledger', accountId] as const,
  invoiceReceipt: (c: string, invoiceId: string) => ['acc', c, 'invoiceReceipt', invoiceId] as const,
  transactions: (c: string, from?: string, to?: string) => ['acc', c, 'transactions', from ?? '', to ?? ''] as const,
  transaction: (c: string, id: string) => ['acc', c, 'transaction', id] as const,
  invoices: (c: string, type: string) => ['acc', c, 'invoices', type] as const,
  invoice: (c: string, id: string) => ['acc', c, 'invoice', id] as const,
  partners: (c: string) => ['acc', c, 'partners'] as const,
  vat: (c: string, from: string, to: string) => ['acc', c, 'vat', from, to] as const,
  categoryTotals: (c: string, from: string, to: string) => ['acc', c, 'categoryTotals', from, to] as const,
  capitalCost: (c: string, from: string, to: string) => ['acc', c, 'capitalCost', from, to] as const,
  capitalCostRates: (c: string) => ['acc', c, 'capitalCostRates'] as const,
  fx: (from: string, to: string, date: string) => ['fx', from, to, date] as const,
  // the market strip is global reference data — no company in the key, so the
  // five rows are fetched once and shared by every company the user switches to
  marketTicker: () => ['marketTicker'] as const,
  // items & BOM — everything under ['items', company]; an item master write invalidates
  // this root and ['stock', company], because the on-hand views join item names and units.
  items: (c: string) => ['items', c] as const,
  itemList: (c: string, filters: unknown = null) => ['items', c, 'list', filters] as const,
  item: (c: string, id: string) => ['items', c, 'item', id] as const,
  itemUoms: (c: string, itemId: string) => ['items', c, 'uoms', itemId] as const,
  itemUomOverrides: (c: string) => ['items', c, 'uomOverrides'] as const,
  itemGroups: (c: string) => ['items', c, 'groups'] as const,
  whereUsed: (c: string, itemId: string) => ['items', c, 'whereUsed', itemId] as const,
  boms: (c: string, itemId: string) => ['items', c, 'boms', itemId] as const,
  bom: (c: string, bomId: string) => ['items', c, 'bom', bomId] as const,
  bomTree: (c: string, bomId: string) => ['items', c, 'bomTree', bomId] as const,
  bomCost: (c: string, bomId: string) => ['items', c, 'bomCost', bomId] as const,
  itemCostCard: (c: string, itemId: string) => ['items', c, 'costCard', itemId] as const,
  documents: (c: string, refType: string, refId: string) => ['items', c, 'documents', refType, refId] as const,
  /** Prefix of every record's attachment list — a document write of unknown ref invalidates it wholesale. */
  documentRefs: (c: string) => ['items', c, 'documents'] as const,
  // the item card's quantities belong to the stock root: a posted movement must
  // refresh them without invalidating the item master
  itemStock: (c: string, itemId: string) => ['stock', c, 'item', itemId, 'byLocation'] as const,
  itemLots: (c: string, itemId: string) => ['stock', c, 'item', itemId, 'lots'] as const,
  itemMovements: (c: string, itemId: string) => ['stock', c, 'item', itemId, 'movements'] as const,
  // stock — the root every quantity movement invalidates
  stock: (c: string) => ['stock', c] as const,
  stockKpis: (c: string) => ['stock', c, 'kpis'] as const,
  stockSettings: (c: string) => ['stock', c, 'settings'] as const,
  // W-06: the item rows and the item × zone cells of the on-hand matrix
  stockSummary: (c: string) => ['stock', c, 'summary'] as const,
  stockMatrix: (c: string) => ['stock', c, 'matrix'] as const,
  // W-07: the movement ledger (one key per filter set), the reason chips and the
  // draft document number the form's subtitle shows
  stockLedger: (c: string, filters: unknown = null) => ['stock', c, 'ledger', filters] as const,
  stockMovement: (c: string, no: string) => ['stock', c, 'movement', no] as const,
  stockReasonCodes: (c: string) => ['stock', c, 'reasonCodes'] as const,
  documentNo: (c: string, docType: string) => ['stock', c, 'documentNo', docType] as const,
  // W-08: the lot browser, one lot by its number, the genealogy per direction and
  // the selected node's panel. All under the stock root, because a movement
  // changes what a lot has left and a new lot appears in the list.
  lots: (c: string) => ['stock', c, 'lots'] as const,
  lotCosts: (c: string) => ['stock', c, 'lotCosts'] as const,
  lot: (c: string, lotNo: string) => ['stock', c, 'lot', lotNo] as const,
  lotTrace: (c: string, lotId: string, direction: string) => ['stock', c, 'trace', lotId, direction] as const,
  traceNode: (c: string, kind: string, refId: string) => ['stock', c, 'traceNode', kind, refId] as const,
  // W-09: the count sessions, one session by its number and its review payload.
  // Under the stock root because approving a count posts movements, and a
  // movement somebody else posts changes what a running count expects.
  counts: (c: string) => ['stock', c, 'counts'] as const,
  count: (c: string, countNo: string) => ['stock', c, 'count', countNo] as const,
  countReview: (c: string, countId: string) => ['stock', c, 'countReview', countId] as const,
  /** M-05: the session payload the phone counts from (`get_stock_count`). */
  countSession: (c: string, countId: string) => ['stock', c, 'countSession', countId] as const,
  /** Prefix of every review, for the realtime invalidation of a running count. */
  countReviews: (c: string) => ['stock', c, 'countReview'] as const,
  // W-31a: balances, counts and movement history of one warehouse — a posted
  // movement has to refresh them, so they hang under the stock root
  warehouseFacts: (c: string, id: string) => ['stock', c, 'warehouse', id, 'facts'] as const,
  openingStock: (c: string) => ['stock', c, 'opening'] as const,
  importPrerequisites: (c: string) => ['stock', c, 'importPrerequisites'] as const,
  // W-31c/d: the wizard's batches. Under their own root because posting one
  // invalidates ['stock'] and ['items'] wholesale, and the history must survive
  // that without a second round trip.
  imports: (c: string) => ['imports', c] as const,
  importBatches: (c: string, kind = 'all') => ['imports', c, 'batches', kind] as const,
  importBatch: (c: string, id: string) => ['imports', c, 'batch', id] as const,
  importRows: (c: string, id: string) => ['imports', c, 'batch', id, 'rows'] as const,
  // the item facts the checklist and the "Değer" column read; an item or a cost
  // write has to refresh them, so they hang under the items root
  importItemFacts: (c: string) => ['items', c, 'importFacts'] as const,
  // the company drawer (şirket evrak arşivi). Its own root: a lot photo uploaded
  // in the warehouse must not make the phone refetch the lease, and renaming a
  // divider must not touch the stock or item caches.
  docs: (c: string) => ['docs', c] as const,
  documentFolders: (c: string) => ['docs', c, 'folders'] as const,
  companyDocuments: (c: string) => ['docs', c, 'company'] as const,
  // reference data kept under the Phase 1 roots so one invalidation covers both shapes
  warehousesFull: (c: string) => ['warehouses', c, 'full'] as const,
  unitUsage: (c: string) => ['units', c, 'usage'] as const,
  workCenters: (c: string) => ['workCenters', c] as const,
};
